import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Listening = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'listening'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container listening-page">
      <h1 className="page-title">Active Listening</h1>

      {/* Include the image */}
      <img
        src="/images/wf-activelistening.webp"
        alt="Active Listening"
        className="page-image"
      />

      <p className="page-text">Welcome to the Active Listening section of Wholeness Forward.</p>

      <p className="page-text">
        Active listening is the practice of fully concentrating, understanding, responding, and remembering what is being said.
        It requires more than just hearing the words; it involves paying attention to the speaker’s message, both verbal and non-verbal, and responding thoughtfully.
      </p>

      <p className="page-text">
        Practicing active listening helps you to build stronger relationships, show empathy, and ensure effective communication.
        It allows for a deeper connection and helps avoid misunderstandings or miscommunications.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Key Components of Active Listening</h2>
      <ul className="content-list">
        <li><strong>Attention:</strong> Give the speaker your full attention without distractions.</li>
        <li><strong>Reflection:</strong> Paraphrase or summarize what the speaker said to ensure understanding.</li>
        <li><strong>Clarification:</strong> Ask open-ended questions to seek further understanding and avoid assumptions.</li>
        <li><strong>Feedback:</strong> Provide constructive feedback based on what you’ve heard.</li>
        <li><strong>Empathy:</strong> Respond with empathy, understanding the speaker’s feelings and perspective.</li>
      </ul>

      <hr className="page-divider" />

      <h3 className="subheading">How to Practice Active Listening</h3>
      <p className="page-text">Here are some practical tips to enhance your active listening skills:</p>
      <ul className="content-list">
        <li><strong>Give your undivided attention:</strong> Put away distractions such as phones, computers, or TV. Make eye contact and focus on the speaker.</li>
        <li><strong>Show that you’re listening:</strong> Use non-verbal cues such as nodding or leaning forward to indicate attentiveness.</li>
        <li><strong>Reflect and summarize:</strong> Repeat back key points the speaker has made. For example: “So what I’m hearing is…” or “If I understand correctly, you’re saying…”.</li>
        <li><strong>Ask open-ended questions:</strong> Encourage the speaker to elaborate by asking questions like, “Can you tell me more about that?” or “How did that make you feel?”</li>
        <li><strong>Avoid interrupting:</strong> Let the speaker finish their thought before responding. Avoid the urge to jump in with your own experiences or advice.</li>
        <li><strong>Respond thoughtfully:</strong> When it’s your turn to speak, offer a thoughtful, empathetic response that shows you’ve fully understood the speaker’s message.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Active Listening Community</h3>
      <p className="page-text">
        Practice active listening with others in our community. Share experiences, ask questions, and learn how to apply these techniques to your personal and professional relationships.
      </p>
      <a href="/forum/Active_Listening" className="btn">Join the Forum</a>
    </div>
  );
};

export default Listening;
